import { useMutation, useQuery } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isMobileOnly } from 'react-device-detect'
import { useNavigate } from 'react-router'
import { Button, FAIcon } from '@/components'
import { DELETE_COMPANY_RULE } from '@/modules/companies/mutations'
import { COMPANY_RULES_QUERY } from '@/modules/companies/queries'
import type {
  CompanyRuleNode,
  CompanyRuleTriggerCapability,
} from '@/modules/companies/types.graphql'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import { Header, OldSection, SectionHeader } from '../common'
import { companyRuleToForm, partSummary } from './company-rule-helpers'

const RULES_LIST_PATH = '/settings/company-rules'

interface CompanyRulesQuery {
  currentCompany: {
    id: string
    ruleCapabilities: CompanyRuleTriggerCapability[]
    rules: CompanyRuleNode[]
  } | null
}

export const CompanyRuleSettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    title: 'settings.company-rules',
    add: 'common.add',
    none: 'common.none',
    inactive: 'common.inactive',
    when: 'settings.rule-when',
    and: 'settings.rule-and',
    then: 'settings.rule-then',
    triggerManual: 'settings.rule-trigger-manual-time-entry-salary-calculation',
    conditionSalaryWorkTime: 'settings.rule-condition-salary-work-time',
    conditionSalaryCode: 'settings.rule-condition-salary-code',
    effectAddSalaryCodeAmount: 'settings.rule-effect-add-salary-code-amount',
    salaryWorkTime: 'settings.rule-condition-salary-work-time',
    salaryCode: 'settings.rule-condition-salary-code',
    amount: 'common.amount',
    deleted: 'settings.company-rule-deleted',
    prompt: {
      delete: ['settings.prompts.delete', { name: '' }],
      deleteTitle: 'settings.prompts.company-rule-delete-title',
    },
  })

  const navigate = useNavigate()
  const confirm = useConfirm()
  const addToast = useToast()
  const onError = useOnErrorAuto()

  const { data } = useQuery<CompanyRulesQuery>(COMPANY_RULES_QUERY)

  const [deleteCompanyRule] = useMutation(DELETE_COMPANY_RULE, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: COMPANY_RULES_QUERY }],
    onCompleted: () => addToast('success', translations.deleted),
    onError: error => onError()(error),
  })

  const rules = data?.currentCompany?.rules ?? []

  function handleCreate() {
    void navigate(`${RULES_LIST_PATH}/create`)
  }

  function handleEdit(rule: CompanyRuleNode) {
    void navigate(`${RULES_LIST_PATH}/${rule.id}`)
  }

  async function handleDelete(rule: CompanyRuleNode) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: rule.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void deleteCompanyRule({
      variables: {
        id: rule.id,
      },
    })
  }

  return (
    <OldSection>
      <Header>
        <SectionHeader>{translations.title}</SectionHeader>
        {isMobileOnly ? (
          <Button size="icon-sm" onClick={handleCreate}>
            <FAIcon icon="plus" />
          </Button>
        ) : (
          <Button size="sm" onClick={handleCreate}>
            <FAIcon icon="plus" />
            {translations.add}
          </Button>
        )}
      </Header>
      <div className="grid gap-3">
        {rules.map(rule => {
          const form = companyRuleToForm(rule)
          return (
            <div
              key={rule.id}
              className="grid grid-cols-[1fr_auto] items-start gap-4 border-b border-quaternary py-4 last:border-b-0"
            >
              <div className="grid gap-3">
                <div className="font-semibold">{rule.name}</div>
                <div className="grid gap-2 text-gray-500">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-primary">
                      {translations.when}
                    </span>
                    {form.trigger && (
                      <span className="inline-flex min-h-7 items-center rounded bg-quaternary px-3 text-primary">
                        {partSummary(form.trigger, translations)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-primary">
                      {translations.and}
                    </span>
                    {form.conditions.length > 0 ? (
                      form.conditions.map(part => (
                        <span
                          key={part.id}
                          className="inline-flex min-h-7 items-center rounded bg-quaternary px-3 text-primary"
                        >
                          {partSummary(part, translations)}
                        </span>
                      ))
                    ) : (
                      <span>{translations.none}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-primary">
                      {translations.then}
                    </span>
                    {form.effects.length > 0 ? (
                      form.effects.map(part => (
                        <span
                          key={part.id}
                          className="inline-flex min-h-7 items-center rounded bg-quaternary px-3 text-primary"
                        >
                          {partSummary(part, translations)}
                        </span>
                      ))
                    ) : (
                      <span>{translations.none}</span>
                    )}
                    {!rule.active && (
                      <span className="inline-flex min-h-7 items-center rounded bg-quaternary px-3 text-primary">
                        {translations.inactive}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-gray-400 hover:text-secondary"
                  onClick={() => handleEdit(rule)}
                >
                  <FAIcon icon="edit" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-gray-400 hover:text-red-500"
                  onClick={() => handleDelete(rule)}
                >
                  <FAIcon icon="times" />
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </OldSection>
  )
}
